import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Container, Row, Col, Button, Modal, Spinner, Alert, Nav } from 'react-bootstrap';
import { ColumnsGap, PersonFill, PersonPlusFill } from 'react-bootstrap-icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useProjects } from '../contexts/ProjectContext';
import { useUsage } from '../contexts/UsageContext';
import { PLAN_LIMITS } from '../types/usage.types';
import { useContextEngine } from '../contexts/ContextEngineContext';
import templateService, { TemplateCategory, Template } from '../services/templateService';
import contextService from '../services/contextService';
import primerService, { Primer, PrimerChip, hashSignals, hashSources } from '../services/primerService';
import { buildDynamicGreeting } from '../utils/dynamicGreeting';
import Footer from './Footer';
import TemplateCard from './TemplateCard';
import ChatContainer from './chat/ChatContainer';
import ProjectLimitModal from './common/ProjectLimitModal';
import ActionChip from './home/ActionChip';
import './Home.css';

// Example prompts shown as ghost text per template when no project context is
// loaded. Keyed by template title (kept in sync with seedTemplates.ts).
const TEMPLATE_GHOST_EXAMPLES: Record<string, string> = {
  'Wireframes': 'Help me wireframe the homepage for a fintech mobile app',
  'Workshops': 'Plan a remote design workshop for a 6-person product team',
  'Ideation': 'Brainstorm onboarding flow ideas for a habit-tracking app',
  'Accessibility Compliance': 'Audit my checkout flow for WCAG 2.2 AA compliance',
  'Design Reviews': 'Review the wireframes for my SaaS dashboard',
  'Interaction Design': 'Design the gesture interactions for a photo editing app',
  'Sitemaps / IA': 'Map the information architecture for an online learning platform',
  'UX Roadmap': 'Build a 6-month UX roadmap for an early-stage marketplace',
  'User Flows': 'Sketch the signup-to-first-value flow for a B2B analytics tool',
  'Summarize Transcripts': 'Summarize this user interview transcript into key insights',
  'Summarize Notes': 'Pull themes from my discovery research notes',
  'Summarize Meetings': 'Summarize this stakeholder meeting into decisions and next steps',
  'Competitive Analysis': 'Compare the onboarding flows of Notion, Linear, and Figma',
  'Research Synthesis': 'Synthesize 8 user interviews into key opportunity areas',
  'Personas': 'Draft personas for a remote-first project management tool',
};

const GENERIC_GHOST_EXAMPLE = "Describe your project or what you'd like to design";

const Home: React.FC = () => {
  const [inputText, setInputText] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [categories, setCategories] = useState<TemplateCategory[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | undefined>();
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [helperPromptText, setHelperPromptText] = useState<string>('');
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(true);
  const [templateError, setTemplateError] = useState<string | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);
  const [showProjectLimitModal, setShowProjectLimitModal] = useState(false);
  const [ghostText, setGhostText] = useState<string | undefined>();
  const [isLoadingGhostText, setIsLoadingGhostText] = useState(false);
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(false);
  const [primer, setPrimer] = useState<Primer | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const ghostTextTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Monotonically-incrementing token used to discard stale dynamic-question
  // responses if the user clicks a different template before the API resolves.
  const questionsRequestIdRef = useRef(0);
  const ghostTextRequestIdRef = useRef(0);
  // Cache of project-specific follow-up questions per (projectId, templateId).
  // Cleared when signals or sources change so newly-added context produces
  // fresh questions on the next click.
  const questionsCacheRef = useRef<Map<string, string[]>>(new Map());
  // Skip the project-switch refresh effect on first commit so it only fires
  // in response to an actual user change, not the initial render.
  const isInitialPromptRefreshRef = useRef(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const { projects, currentProject, fetchProjects, selectProject } = useProjects();
  const { getTemplateLimitMessage, usageStats } = useUsage();
  const {
    registerSignalClickHandler,
    readinessScore,
    signals,
    sources,
    loadingSignals,
    loadingSources,
  } = useContextEngine();
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(currentProject?.id ?? null);
  const contextLoaded =
    !!currentProject && currentProject.id === selectedProjectId && signals.length > 0;

  // Register signal chip click handler to insert label into chat input
  useEffect(() => {
    registerSignalClickHandler((signal) => {
      setInputText((prev) => (prev ? `${prev} ${signal.label}` : signal.label));
    });
    return () => registerSignalClickHandler(null);
  }, [registerSignalClickHandler]);

  // Keep local selectedProjectId in sync when global currentProject changes externally
  useEffect(() => {
    setSelectedProjectId(currentProject?.id ?? null);
  }, [currentProject]);

  // Sync local project selection to the global ProjectContext
  const handleProjectChange = useCallback((projectId: string | null) => {
    setSelectedProjectId(projectId);
    if (projectId) {
      const project = projects.find(p => p.id === projectId);
      selectProject(project ?? null);
    } else {
      selectProject(null);
    }
  }, [projects, selectProject]);

  useEffect(() => {
    fetchTemplates();
    if (currentUser) {
      fetchProjects();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  useEffect(() => {
    // Set preselected project if passed from navigation state
    const state = location.state as { preselectedProjectId?: string };
    if (state?.preselectedProjectId) {
      handleProjectChange(state.preselectedProjectId);
      // Clear the state to prevent reapplying on subsequent renders
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);


  // Fetch ghost text when both project and template are selected
  const fetchGhostText = useCallback(async (projectId: string, templateId: string) => {
    const requestId = ++ghostTextRequestIdRef.current;
    try {
      const result = await contextService.getGhostText(projectId, templateId);
      if (requestId !== ghostTextRequestIdRef.current) return;
      setGhostText(result.suggestion || undefined);
    } catch {
      // Ghost text is optional — silently fail
    } finally {
      if (requestId === ghostTextRequestIdRef.current) {
        setIsLoadingGhostText(false);
      }
    }
  }, []);

  useEffect(() => {
    // Clear any pending timer
    if (ghostTextTimerRef.current) {
      clearTimeout(ghostTextTimerRef.current);
      ghostTextTimerRef.current = null;
    }

    if (contextLoaded && selectedTemplateId) {
      // Invalidate any in-flight request and clear the previous template's
      // suggestion immediately so the spinner can show during the debounce
      // window and a stale response can't overwrite the new one.
      ghostTextRequestIdRef.current++;
      setGhostText(undefined);
      setIsLoadingGhostText(true);
      ghostTextTimerRef.current = setTimeout(() => {
        fetchGhostText(selectedProjectId!, selectedTemplateId);
      }, 300);
    } else if (contextLoaded && !selectedTemplateId && primer?.ghostText) {
      // No template chosen yet — surface the primer's no-template opener so the
      // input feels primed the moment the user lands on /home post-onboarding.
      setGhostText(primer.ghostText);
      setIsLoadingGhostText(false);
    } else if (!contextLoaded && selectedTemplate?.title) {
      setGhostText(TEMPLATE_GHOST_EXAMPLES[selectedTemplate.title] ?? GENERIC_GHOST_EXAMPLE);
      setIsLoadingGhostText(false);
    } else if (!contextLoaded && !selectedTemplateId) {
      setGhostText(GENERIC_GHOST_EXAMPLE);
      setIsLoadingGhostText(false);
    } else {
      setGhostText(undefined);
      setIsLoadingGhostText(false);
    }

    return () => {
      if (ghostTextTimerRef.current) {
        clearTimeout(ghostTextTimerRef.current);
      }
    };
  }, [selectedProjectId, selectedTemplateId, selectedTemplate, contextLoaded, fetchGhostText, primer]);

  // Fetch the home-page primer (no-template ghost text + 3 action chips) once
  // the user has a project selected with extracted signals. The service caches
  // by signal/source hash in localStorage, so repeat mounts hit the cache and
  // adding a new signal forces a regeneration on next mount.
  useEffect(() => {
    if (!selectedProjectId) {
      setPrimer(null);
      return;
    }
    if (currentProject?.id !== selectedProjectId) return;
    if (loadingSignals || loadingSources) return;
    // Only treat the project as genuinely empty when both stores are empty.
    // If sources are present but signals haven't arrived yet (or vice-versa),
    // wait — bailing here would prematurely null the primer mid-flip.
    if (signals.length === 0 && sources.length === 0) {
      setPrimer(null);
      return;
    }
    if (signals.length === 0) return;

    let cancelled = false;
    const sHash = hashSignals(signals);
    const srcHash = hashSources(sources);
    primerService.getPrimer(selectedProjectId, sHash, srcHash).then((result) => {
      if (cancelled) return;
      setPrimer(result);
    });
    return () => {
      cancelled = true;
    };
  }, [selectedProjectId, currentProject?.id, signals, sources, loadingSignals, loadingSources]);

  // Invalidate the per-template question cache whenever the project or its
  // loaded context changes. Adding a new signal or document should produce
  // fresh project-specific questions on the next click instead of a stale
  // cached set. Use identity (not length) so swaps with equal counts also
  // invalidate.
  useEffect(() => {
    questionsCacheRef.current.clear();
  }, [selectedProjectId, signals, sources]);

  // Builds the dynamic greeting + kicks off the project-specific question
  // fetch for the given template. Used both by the click handler and by the
  // project-switch refresh effect, so a project change while a template stays
  // selected reruns the same logic that a fresh click would.
  const applyDynamicPromptForTemplate = useCallback(
    (template: Template): { promptToShow: string } => {
      const projectMatches =
        selectedProjectId !== null && currentProject?.id === selectedProjectId;

      const cacheKey =
        projectMatches && template.id ? `${selectedProjectId}-${template.id}` : null;
      const cachedQuestions =
        cacheKey ? questionsCacheRef.current.get(cacheKey) : undefined;

      const dynamicGreeting = projectMatches
        ? buildDynamicGreeting({
            templateTitle: template.title,
            readinessScore,
            signals,
            sources,
            dynamicQuestions: cachedQuestions,
          })
        : null;

      const promptToShow = dynamicGreeting ?? template.helperPrompt ?? '';
      setHelperPromptText(promptToShow);

      if (dynamicGreeting && projectMatches && template.id && !cachedQuestions) {
        const requestId = ++questionsRequestIdRef.current;
        const projectIdAtCall = selectedProjectId as string;
        const templateIdAtCall = template.id;
        const capturedSignals = signals;
        const capturedSources = sources;
        const capturedReadiness = readinessScore;
        const cacheKeyAtCall = `${projectIdAtCall}-${templateIdAtCall}`;

        setIsLoadingQuestions(true);

        contextService
          .getTemplateQuestions(projectIdAtCall, templateIdAtCall)
          .then((result) => {
            if (requestId !== questionsRequestIdRef.current) return;
            if (!result.questions || result.questions.length === 0) return;
            questionsCacheRef.current.set(cacheKeyAtCall, result.questions);
            const upgraded = buildDynamicGreeting({
              templateTitle: template.title,
              readinessScore: capturedReadiness,
              signals: capturedSignals,
              sources: capturedSources,
              dynamicQuestions: result.questions,
            });
            if (upgraded) setHelperPromptText(upgraded);
          })
          .catch(() => {
            // Network or backend error — keep the static fallback already shown.
          })
          .finally(() => {
            if (requestId === questionsRequestIdRef.current) {
              setIsLoadingQuestions(false);
            }
          });
      }

      return { promptToShow };
    },
    [selectedProjectId, currentProject, signals, sources, readinessScore]
  );

  // Refresh the helper prompt when the project changes while a template is
  // already selected. Without this, the user has to deselect/reselect the
  // template to see questions tailored to the new project. Gated on
  // currentProject?.id matching selectedProjectId and on ContextEngine no
  // longer loading, so buildDynamicGreeting runs against the new project's
  // signals/sources, not stale ones from the previous project.
  useEffect(() => {
    if (isInitialPromptRefreshRef.current) {
      isInitialPromptRefreshRef.current = false;
      return;
    }
    if (!selectedTemplate) return;
    if (selectedProjectId) {
      if (currentProject?.id !== selectedProjectId) return;
      if (loadingSignals || loadingSources) return;
    }
    applyDynamicPromptForTemplate(selectedTemplate);
  }, [
    selectedProjectId,
    currentProject?.id,
    signals,
    sources,
    readinessScore,
    selectedTemplate,
    loadingSignals,
    loadingSources,
    applyDynamicPromptForTemplate,
  ]);

  const fetchTemplates = async () => {
    try {
      setIsLoadingTemplates(true);
      setTemplateError(null);
      const data = await templateService.getCategoriesWithTemplates();
      
      if (data.length === 0) {
        console.log('No templates from API.');
      } else {
        setCategories(data);
        setSelectedCategory(data[0].name);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      setTemplateError('Failed to load templates.');
    } finally {
      setIsLoadingTemplates(false);
    }
  };

  const getCurrentCategoryTemplates = () => {
    const category = categories.find(cat => cat.name === selectedCategory);
    return category?.templates || [];
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const validateFile = (file: File): string | null => {
    const allowedTypes = ['application/pdf', 'text/plain', 'image/png', 'image/jpeg', 'image/jpg'];
    const maxSize = 30 * 1024 * 1024; // 30MB in bytes

    if (!allowedTypes.includes(file.type)) {
      return `Invalid file type: ${file.name}. Only PDF, TXT, PNG, JPG, and JPEG files are allowed.`;
    }

    if (file.size > maxSize) {
      return `File too large: ${file.name}. Maximum size is 30MB.`;
    }

    return null;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setFileError(null);

    // Chat attachments piggyback on the sourcesPerProject cap to keep plan limits
    // in one place. If these ever need to diverge, add a dedicated filesPerChat
    // field to PlanLimits.
    const planName = usageStats?.plan ?? 'starter';
    const maxFiles = PLAN_LIMITS[planName].sourcesPerProject ?? 5;

    // Calculate how many files we can add
    const remainingSlots = maxFiles - uploadedFiles.length;
    const filesToAdd = files.slice(0, remainingSlots);
    const rejectedCount = files.length - filesToAdd.length;

    // Validate each file that we're adding
    const validFiles: File[] = [];
    for (const file of filesToAdd) {
      const error = validateFile(file);
      if (error) {
        setFileError(error);
        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }
      validFiles.push(file);
    }

    // Add valid files
    setUploadedFiles(prev => [...prev, ...validFiles]);

    // Show error if some files were rejected due to limit
    if (rejectedCount > 0) {
      setFileError(`Maximum ${maxFiles} files allowed per chat session. ${rejectedCount} file(s) were not added.`);
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveFile = (index: number) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
    setFileError(null);
  };

  const renderInline = (line: string): React.ReactNode => {
    // Split on **bold** spans, preserving them as capture groups.
    const parts = line.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, idx) => {
      const m = part.match(/^\*\*(.+)\*\*$/);
      return m ? <strong key={idx}>{m[1]}</strong> : <React.Fragment key={idx}>{part}</React.Fragment>;
    });
  };

  const formatHelperPrompt = (text: string): React.ReactNode => {
    const lines = text.split(/\\n|\n/);
    const blocks: React.ReactNode[] = [];
    let bulletBuffer: string[] = [];

    const flushBullets = () => {
      if (bulletBuffer.length === 0) return;
      blocks.push(
        <ul key={`ul-${blocks.length}`}>
          {bulletBuffer.map((b, i) => (
            <li key={i}>{renderInline(b)}</li>
          ))}
        </ul>
      );
      bulletBuffer = [];
    };

    for (const raw of lines) {
      const line = raw.trim();
      if (line.startsWith('- ')) {
        bulletBuffer.push(line.slice(2));
      } else if (line.length > 0) {
        flushBullets();
        blocks.push(<p key={`p-${blocks.length}`}>{renderInline(line)}</p>);
      }
    }
    flushBullets();

    return <>{blocks}</>;
  };

  const handlePrimerChipClick = (chip: PrimerChip) => {
    if (!currentUser) {
      setShowAuthModal(true);
      return;
    }
    let template: Template | undefined;
    for (const cat of categories) {
      template = cat.templates?.find((t) => t.id === chip.templateId);
      if (template) break;
    }
    if (!template) {
      // Template no longer available (plan downgrade, deletion) — fall back to
      // just filling the input so the click still does something useful.
      setInputText(chip.prompt);
      return;
    }
    setSelectedTemplate(template);
    setSelectedTemplateId(template.id);
    applyDynamicPromptForTemplate(template);
    setInputText(chip.prompt);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputText.trim() || isCreating) return;

    if (!currentUser) {
      setShowAuthModal(true);
      return;
    }

    setIsCreating(true);

    // Generate a temporary conversation ID for immediate navigation
    const tempConversationId = `temp_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;

    // Navigate immediately with the user's prompt and template context
    const navigationState: any = {
      userPrompt: inputText,
      templateId: selectedTemplateId,
      isCreating: true
    };

    // Add selected project if available
    if (selectedProjectId) {
      navigationState.projectId = selectedProjectId;
    }

    // Only add uploadedFiles if there are any
    if (uploadedFiles.length > 0) {
      navigationState.uploadedFiles = uploadedFiles;
    }

    // Add template context if available
    if (selectedTemplate && helperPromptText) {
      // Swap the static helper for a context-aware greeting when onboarding
      // signals/sources exist on the currently-selected project. Gate on
      // currentProject matching selectedProjectId so we don't leak signals
      // from a stale ContextEngine state during a project switch race.
      const projectMatches =
        selectedProjectId !== null && currentProject?.id === selectedProjectId;
      const dynamicGreeting = projectMatches
        ? buildDynamicGreeting({
            templateTitle: selectedTemplate.title,
            readinessScore,
            signals,
            sources,
          })
        : null;

      navigationState.templateTitle = selectedTemplate.title;
      navigationState.helperPrompt = dynamicGreeting ?? helperPromptText;
      navigationState.initialPrompt = `Help me with ${selectedTemplate.title.toLowerCase()}`;
    }

    navigate(`/chat/${tempConversationId}`, { state: navigationState });

    setSelectedTemplateId(undefined);
    setSelectedTemplate(null);
    setHelperPromptText('');
    setInputText('');
    setUploadedFiles([]);
    setFileError(null);
    setIsCreating(false);
  };

  const handleTemplateClick = async (template: Template) => {

    if (!currentUser) {
      setShowAuthModal(true);
      return;
    }

    // If clicking on the already selected template, deselect it
    if (selectedTemplateId === template.id) {
      setSelectedTemplate(null);
      setSelectedTemplateId(undefined);
      // Don't clear user's input when deselecting - only clear the helper prompt
      setHelperPromptText('');
      // Invalidate any in-flight questions request and clear loading state
      questionsRequestIdRef.current++;
      setIsLoadingQuestions(false);
      return;
    }

    setSelectedTemplate(template);
    setSelectedTemplateId(template.id);

    const { promptToShow } = applyDynamicPromptForTemplate(template);

    if (promptToShow) {
      // Don't clear existing user input - preserve it when selecting a template
      setTimeout(() => {
        const chatContainer = document.querySelector('.chat-container-home');
        if (chatContainer) {
          chatContainer.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    } else if (!inputText.trim()) {
      // Only set default text if user hasn't typed anything yet
      setInputText(`Help me with ${template.title.toLowerCase()}`);
    }
  };

  const handleSignIn = () => {
    setShowAuthModal(false);
    navigate('/signin');
  };

  const handleSignUp = () => {
    setShowAuthModal(false);
    navigate('/register');
  };

  return (
    <div className="home-container">
      <div className="main-content">
        <Container>
          <Row className="mt-5">
            <Col>
              <div className="text-start mb-3">
                {contextLoaded ? (
                  <>
                    <h1>Your context is loaded.</h1>
                    <h1>Revealer knows your goals, constraints, and your target - ask it anything.</h1>
                    <p className="lead">You are working on {currentProject?.name}.</p>
                  </>
                ) : (
                  <>
                    <h1>good afternoon.</h1>
                    <h1>let's get creating.</h1>
                    <p className="lead">Start fresh - describe your project or pick a template below.</p>
                  </>
                )}
              </div>
            </Col>
          </Row>
          <Row>
            <Col>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept=".pdf,.txt,.png,.jpg,.jpeg"
                multiple
                className="home-file-input-hidden"
              />
              <ChatContainer
                inputText={inputText}
                onInputChange={setInputText}
                onSubmit={handleSubmit}
                onUpload={handleUploadClick}
                selectedProjectId={selectedProjectId}
                onProjectChange={handleProjectChange}
                onProjectCreated={(p) => setSelectedProjectId(p.id)}
                onLimitError={() => setShowProjectLimitModal(true)}
                projects={projects}
                placeholder={helperPromptText ? "HOW CAN I HELP YOU TODAY?" : "HOW CAN I HELP YOU TODAY?"}
                uploadedFiles={uploadedFiles}
                onRemoveFile={handleRemoveFile}
                fileError={fileError}
                ghostText={ghostText}
                isLoadingGhostText={isLoadingGhostText}
                onGhostTextAccept={() => setGhostText(undefined)}
              />
            </Col>
          </Row>
          <Row className="mt-2">
            <Col className="text-start">
              {isLoadingTemplates ? (
                <div className="d-flex justify-content-center align-items-center templates-loading-container">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading templates...</span>
                  </Spinner>
                </div>
              ) : categories.length > 0 ? (
                <>
                  <p className="mb-3 helper"><ColumnsGap size={16} /> Need ideas? Select a template to get started.</p>
                  <Nav variant="tabs" activeKey={selectedCategory} className="template-category-nav">
                    {categories.map(category => (
                      <Nav.Item key={category.name}>
                        <Nav.Link
                          eventKey={category.name}
                          onClick={() => setSelectedCategory(category.name)}
                        >
                          {category.name}
                        </Nav.Link>
                      </Nav.Item>
                    ))}
                  </Nav>
                </>
              ) : null}
            </Col>
          </Row>
          {!isLoadingTemplates && categories.length > 0 && (
            <Row className="mt-4">
              <Col>
                <div className="d-flex flex-wrap gap-3">
                  {getCurrentCategoryTemplates().map((template, index) => (
                    <TemplateCard
                      key={`${selectedCategory}-${index}`}
                      title={template.title}
                      description={template.description}
                      icon={template.icon}
                      onClick={() => handleTemplateClick(template)}
                      isSelected={selectedTemplateId === template.id}
                      isDisabled={template.isAvailable === false}
                      disabledTooltip={getTemplateLimitMessage()}
                    />
                  ))}
                </div>
              </Col>
            </Row>
          )}
          {templateError && (
            <Row>
              <Col>
                <Alert variant="warning" className="mt-3">
                  {templateError}
                </Alert>
              </Col>
            </Row>
          )}
          {helperPromptText && selectedTemplate && (
            <Row className="mt-4">
              <Col>
                <div className="helper-prompt-display">
                  {isLoadingQuestions ? (
                    <div className="helper-prompt-loading" role="status" aria-live="polite">
                      <Spinner animation="border" size="sm" />
                      <span>Tailoring to your project…</span>
                    </div>
                  ) : (
                    <div className="helper-prompt-content">
                      {formatHelperPrompt(helperPromptText)}
                    </div>
                  )}
                </div>
              </Col>
            </Row>
          )}
          {!!selectedProjectId &&
            !selectedTemplateId &&
            !inputText.trim() &&
            primer &&
            primer.chips.length > 0 && (
              <Row className="mt-4">
                <Col>
                  <span className="action-chips-label">Get started with one of these actions</span>
                  <div className="action-chips-row">
                    {primer.chips.map((chip, idx) => (
                      <ActionChip
                        key={`${chip.templateId}-${idx}`}
                        label={chip.label}
                        onClick={() => handlePrimerChipClick(chip)}
                      />
                    ))}
                  </div>
                </Col>
              </Row>
            )}
        </Container>
      </div>
      <Footer />

      <Modal show={showAuthModal} onHide={() => setShowAuthModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Sign In Required</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-center mb-4">
            You need to be signed in to start a conversation with Revealer.
          </p>
          <div className="d-grid gap-2">
            <Button variant="primary" size="lg" onClick={handleSignIn}>
              <PersonFill className="me-2" />
              Sign In
            </Button>
            <Button variant="outline-primary" size="lg" onClick={handleSignUp}>
              <PersonPlusFill className="me-2" />
              Create Account
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      <ProjectLimitModal
        show={showProjectLimitModal}
        onHide={() => setShowProjectLimitModal(false)}
        planName={usageStats?.plan || 'starter'}
      />
    </div>
  );
};

export default Home;